import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdEdit, MdEvent, MdSearch } from 'react-icons/md';
import PageHeader from '../../components/owner/PageHeader';
import {
  ColumnFilterHeader,
  cellValue,
  matchSelectFilter,
  uniqueColumnOptions,
  useColumnFilters,
} from '../../components/admin/ColumnFilterHeader';
import { usePanelToast } from '../../context/PanelToastContext';
import {
  fetchClasses,
  saveClass,
} from '../../services/receptionist/receptionistApi';
import {
  exportClassCsv,
  exportClassExcel,
  exportClassPdf,
  exportClasses,
  printClassList,
} from './exportClasses';
import { instantLoadState } from '../../utils/instantLoad';
import { peekGymCache } from '../../services/cache/gymCache';
import { dedupeByName } from '../../utils/dedupeList';
import '../owner/ownerPages.css';
import '../owner/subscriptions/subscriptions.css';
import '../admin/memberships/memberships.css';
import '../admin/adminListPages.css';
import './members/members.css';

function getTodayDateStr() {
  return new Date().toISOString().slice(0, 10);
}

function getEmptyClassForm() {
  return {
    name: '',
    startDate: getTodayDateStr(),
    endDate: '',
    description: '',
  };
}

function Classes() {
  const toast = usePanelToast();
  const classBootstrap = instantLoadState('classes');
  const [list, setList] = useState(classBootstrap.initialData);
  const [loading, setLoading] = useState(classBootstrap.initialLoading);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [classForm, setClassForm] = useState(getEmptyClassForm);
  const [editingId, setEditingId] = useState('');
  const [search, setSearch] = useState('');
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    name: 'all',
    startDate: 'all',
    endDate: 'all',
    description: 'all',
  });

  const loadList = useCallback(async () => {
    try {
      setError('');
      if (!peekGymCache('classes')) setLoading(true);
      setList(dedupeByName(await fetchClasses()));
    } catch (err) {
      setError(err.message || 'Failed to load classes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const filteredList = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matched = list.filter((c) => {
      if (query) {
        const matchesSearch = (
          c.name.toLowerCase().includes(query) ||
          c.startDate.includes(query) ||
          c.endDate.includes(query) ||
          (c.description || '').toLowerCase().includes(query)
        );
        if (!matchesSearch) return false;
      }
      return (
        matchSelectFilter(cellValue(c.name), filters.name)
        && matchSelectFilter(cellValue(c.startDate), filters.startDate)
        && matchSelectFilter(cellValue(c.endDate), filters.endDate)
        && matchSelectFilter(cellValue(c.description), filters.description)
      );
    });
    return dedupeByName(matched);
  }, [list, search, filters]);

  const openModal = () => {
    setFormKey((k) => k + 1);
    setEditingId('');
    setClassForm(getEmptyClassForm());
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setFormKey((k) => k + 1);
    setEditingId(item.id);
    setClassForm({
      name: item.name,
      startDate: item.startDate,
      endDate: item.endDate,
      description: item.description || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId('');
  };

  const handleSaveClass = async (e) => {
    e.preventDefault();
    if (!classForm.name.trim() || !classForm.startDate || !classForm.endDate) return;

    const record = {
      name: classForm.name.trim(),
      startDate: classForm.startDate,
      endDate: classForm.endDate,
      description: classForm.description.trim(),
    };

    try {
      if (editingId) {
        const updated = await saveClass(record, editingId);
        setList((prev) => prev.map((c) => (c.id === editingId ? updated : c)));
        toast.showUpdated('Class');
      } else {
        const created = await saveClass(record);
        setList((prev) => [created, ...prev]);
        toast.showAdded('Class');
      }
      closeModal();
    } catch (err) {
      const message = err.message || 'Failed to save class';
      setError(message);
      toast.showError(message);
    }
  };

  return (
    <div>
      <PageHeader title="Classes" subtitle="Add and manage gym classes" variant="banner">
        <button type="button" className="owner-btn owner-btn-primary" onClick={openModal}>
          <MdEvent style={{ marginRight: 8 }} />
          Add Class
        </button>
      </PageHeader>

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading classes...</p>}

      <div className="rec-member-toolbar">
        <div className="rec-member-toolbar-left">
          <div className="rec-member-search-field">
            <MdSearch className="rec-member-search-icon" />
            <input
              type="text"
              className="owner-form-input rec-member-search-input"
              placeholder="Search classes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="rec-member-toolbar-actions">
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => printClassList(filteredList)}>
            Print
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportClassCsv(filteredList)}>
            Export CSV
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportClassPdf(filteredList)}>
            Export PDF
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportClassExcel(filteredList)}>
            Export Excel
          </button>
          <button type="button" className="owner-btn owner-btn-primary owner-btn-sm" onClick={() => exportClasses(filteredList)}>
            Export
          </button>
        </div>
      </div>

      <div className="owner-card rec-panel-card rec-member-list-card">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Name"
                  filterKey="name"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (c) => c.name)}
                  allLabel="All Names"
                />
                <ColumnFilterHeader
                  label="Start Date"
                  filterKey="startDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (c) => c.startDate)}
                  allLabel="All Start Dates"
                />
                <ColumnFilterHeader
                  label="End Date"
                  filterKey="endDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (c) => c.endDate)}
                  allLabel="All End Dates"
                />
                <ColumnFilterHeader
                  label="Description"
                  filterKey="description"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (c) => c.description)}
                  allLabel="All Descriptions"
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.length > 0 ? (
                filteredList.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{c.startDate}</td>
                    <td>{c.endDate}</td>
                    <td>{c.description || '—'}</td>
                    <td>
                      <div className="rec-member-actions-cell">
                        <button
                          type="button"
                          className="rec-member-icon-btn rec-member-icon-btn-edit"
                          onClick={() => openEditModal(c)}
                          aria-label={`Edit ${c.name}`}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="owner-placeholder-text">No classes match your search.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="subscription-modal-overlay" onClick={closeModal}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-class-title"
          >
            <div className="subscription-modal-header">
              <h2 id="add-class-title" className="subscription-modal-title">{editingId ? 'Edit Class' : 'Add Class'}</h2>
              <button type="button" className="subscription-modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <form key={formKey} className="subscription-form" onSubmit={handleSaveClass}>
              <div className="owner-grid-2">
                <div className="owner-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="owner-form-label" htmlFor="class-name">Name</label>
                  <input
                    id="class-name"
                    className="owner-form-input"
                    value={classForm.name}
                    onChange={(e) => setClassForm((p) => ({ ...p, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="class-start-date">Start Date</label>
                  <input
                    id="class-start-date"
                    type="date"
                    className="owner-form-input"
                    value={classForm.startDate}
                    onChange={(e) => setClassForm((p) => ({ ...p, startDate: e.target.value }))}
                    required
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="class-end-date">End Date</label>
                  <input
                    id="class-end-date"
                    type="date"
                    className="owner-form-input"
                    value={classForm.endDate}
                    min={classForm.startDate}
                    onChange={(e) => setClassForm((p) => ({ ...p, endDate: e.target.value }))}
                    required
                  />
                </div>
                <div className="owner-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="owner-form-label" htmlFor="class-description">Description</label>
                  <textarea
                    id="class-description"
                    className="owner-form-input owner-form-textarea"
                    rows={4}
                    value={classForm.description}
                    onChange={(e) => setClassForm((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Class details, schedule notes, etc."
                  />
                </div>
              </div>
              <div className="subscription-form-actions">
                <button type="button" className="owner-btn owner-btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="owner-btn owner-btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default Classes;
